import logger from "../utils/logger";

/**
 * Token storage abstraction layer.
 *
 * Current mode: "localStorage" (tokens managed by frontend)
 * Future mode: "httpOnly" (tokens managed by backend via Set-Cookie)
 *
 * When migrating to HttpOnly cookies:
 * 1. Backend sets tokens via Set-Cookie headers (HttpOnly; Secure; SameSite=Strict)
 * 2. Change STORAGE_MODE to "httpOnly"
 * 3. Configure axios to send withCredentials: true
 * 4. Remove Authorization header injection from interceptors
 * 5. Add /app/auth/status endpoint check for isAuthenticated
 */
const STORAGE_MODE = "localStorage"; // Change to "httpOnly" when backend supports it

const tokenStorage = {
  getAccessToken: () => {
    if (STORAGE_MODE === "httpOnly") {
      // In httpOnly mode, token is in cookie -- JS can't read it.
      // Return null; axios sends cookie automatically via withCredentials.
      return null;
    }
    return localStorage.getItem("accessToken");
  },

  setAccessToken: (token) => {
    if (STORAGE_MODE === "httpOnly") {
      // In httpOnly mode, backend sets cookie via Set-Cookie header.
      // Nothing for frontend to do.
      logger.debug("httpOnly mode: token set by backend cookie");
      return;
    }
    if (token) {
      localStorage.setItem("accessToken", token);
    } else {
      localStorage.removeItem("accessToken");
    }
  },

  getRefreshToken: () => {
    if (STORAGE_MODE === "httpOnly") {
      return null;
    }
    return localStorage.getItem("refreshToken");
  },

  setRefreshToken: (token) => {
    if (STORAGE_MODE === "httpOnly") {
      return;
    }
    if (token) {
      localStorage.setItem("refreshToken", token);
    } else {
      localStorage.removeItem("refreshToken");
    }
  },

  clearTokens: () => {
    if (STORAGE_MODE === "httpOnly") {
      // In httpOnly mode, logout must call a backend endpoint that
      // clears the cookie (Set-Cookie with maxAge=0)
      logger.debug("httpOnly mode: tokens cleared by backend");
      return;
    }
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  },

  /**
   * Whether the frontend needs to manually set Authorization headers.
   * In httpOnly mode, cookies are sent automatically.
   */
  requiresAuthHeader: () => STORAGE_MODE !== "httpOnly",

  /**
   * Whether axios should include credentials (cookies) in requests.
   */
  withCredentials: () => STORAGE_MODE === "httpOnly",
};

export default tokenStorage;
